import { Avatar, Button, Col, Modal, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiMoreHorizontal,
  FiRefreshCw
} from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import TeamPicker from 'screens/Admin/screens/QuickQuiz/components/TeamPicker';
import {
  deleteKeyOf,
  FANWAVE_MODE,
  fmtCount,
  GAME_DAY_SCHEDULE,
  isTerminalStatus,
  missingTapTargetReason,
  orderWavesByStart,
  queuedWaveIds,
  resolveTapTargetBounds,
  REWARDS_SOURCE,
  validateTapTarget,
  WAVE_STATUS
} from '../constants';
import { scheduleLine } from '../scheduleLine';
import { Badge, blockNonInteger, MATCH_ACCENT, STATUS_BADGE_VARIANT, toDigits } from './ui';

/**
 * Unified wave list — GET /admin/fanwave/list. Game Day and Non-Game Day waves across their
 * whole lifecycle, filterable by team/mode/status, and the place the whole lifecycle is run
 * from: a wave exists only because an admin created it here, so every row carries the actions
 * its state allows.
 *
 * Each row shows Participants and Edit inline; the state-specific actions sit in its overflow
 * menu (⋯), which is what a row can do right now:
 *
 *   scheduled Game Day     → Edit · Disable, Delete
 *   active Game Day        → Edit (end/target/rewards) · Apply target, Disable, Delete (409s
 *                            until disabled)
 *   completed Game Day     → Delete
 *   scheduled Non-Game Day → Edit · Cancel, Delete
 *   active Non-Game Day    → Edit (end/target/rewards) · Apply target, Cancel, Delete (409s
 *                            until cancelled)
 *   terminal Non-Game Day  → Delete
 *
 * Edit is always rendered — disabled with the reason on a wave too far gone to update — so the
 * rule is visible instead of the button disappearing.
 *
 * Identity: every wave is addressed by its own `waveId`, in BOTH modes. `gameId` says which
 * MATCH a Game Day wave runs on and is shared by all of that match's waves, so it identifies
 * nothing on its own — it is only the fallback for a legacy archive row that has no waveId (see
 * `deleteKeyOf`).
 *
 * A match can hold several waves, running one after another. They are shown together under the
 * fixture, in the order they open, and the ones waiting for the wave ahead of them are marked
 * QUEUED — created and editable, but not yet on air.
 */

const PAGE_LIMIT = 20;

const MODE_OPTIONS = [
  { value: 'All', opt: undefined },
  { value: 'Game Day', opt: FANWAVE_MODE.GAME_DAY },
  { value: 'Non-Game Day', opt: FANWAVE_MODE.NON_GAME_DAY }
];

const STATUS_OPTIONS = [
  { value: 'All', opt: undefined },
  { value: 'Scheduled', opt: WAVE_STATUS.SCHEDULED },
  { value: 'Active', opt: WAVE_STATUS.ACTIVE },
  { value: 'Completed', opt: WAVE_STATUS.COMPLETED },
  { value: 'Cancelled', opt: WAVE_STATUS.CANCELLED },
  { value: 'Expired', opt: WAVE_STATUS.EXPIRED }
];

/** The literal an admin types to confirm a delete. */
const DELETE_KEYWORD = 'DELETE';

/** Bounds for the retarget field — the API's own min/max/step (see resolveTapTargetBounds). */
const TAP_TARGET_BOUNDS = resolveTapTargetBounds(null);

// A 5xx comes back without a message (`{ status }`), so every handler needs a fallback —
// the API's own 400/404/409 text is always preferred, it is written for admins.
const errorOf = (res, fallback) => res?.err || fallback;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  background: #fff;
  color: ${theme.GREY_70};
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  svg {
    ${(props) =>
      props.$spinning &&
      css`
        animation: ${spin} 0.8s linear infinite;
      `}
  }
`;

const RowActionButton = styled.button`
  height: 30px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.$danger ? '#F8D4D4' : theme.GREY_30)};
  border-radius: 8px;
  background: #fff;
  color: ${(props) => (props.$danger ? '#DC2626' : theme.GREY_70)};
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    border-color: ${(props) => (props.$danger ? '#DC2626' : theme.SECONDARY)};
    color: ${(props) => (props.$danger ? '#DC2626' : theme.SECONDARY)};
    background: ${(props) => (props.$danger ? '#FDECEC' : '#fff')};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

// Overflow menu for the state-specific actions. Five buttons abreast wrapped into an
// unreadable block, so a row now shows the two everyday actions and tucks the rest here.
const MenuWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const MenuTrigger = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => (props.$open ? theme.SECONDARY : theme.GREY_30)};
  border-radius: 8px;
  background: ${(props) => (props.$open ? theme.SECONDARY_10 : '#fff')};
  color: ${(props) => (props.$open ? theme.SECONDARY : theme.GREY_70)};
  cursor: pointer;

  &:hover {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }
`;

const ActionMenu = styled.div`
  position: absolute;
  top: 34px;
  right: 0;
  z-index: 6;
  min-width: 196px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid ${theme.GREY_20};
  border-radius: 10px;
  box-shadow: 0 10px 26px rgba(20, 28, 40, 0.14);
`;

const MenuItem = styled.button`
  height: 34px;
  padding: 0 10px;
  border: none;
  border-radius: 7px;
  background: none;
  text-align: left;
  font-family: CeraPro;
  font-size: 12.5px;
  font-weight: 600;
  color: ${(props) => (props.$danger ? '#DC2626' : theme.GREY_80)};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$danger ? '#FDECEC' : theme.GREY_10)};
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  margin: 5px 4px;
  background: ${theme.GREY_20};
`;

// Why a menu item is disabled, spelled out — a greyed-out row with no reason reads as a bug.
const MenuNote = styled.div`
  padding: 2px 10px 6px;
  max-width: 240px;
  font-family: CeraPro;
  font-size: 11px;
  line-height: 1.45;
  color: ${theme.GREY_60};
`;

// Clickable wave row — the whole card opens the read-only config detail panel. Buttons
// inside stop propagation so they run their own action instead.
const WaveCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid ${theme.GREY_20};
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;

  &:hover {
    border-color: ${theme.SECONDARY_20};
    box-shadow: 0 2px 8px rgba(20, 28, 40, 0.06);
  }
`;

/**
 * A match's waves, enclosed. Several waves on one fixture only make sense read together — wave 1
 * hands over to wave 2 — so they sit in one tinted panel behind a left rail, instead of as a run of
 * cards nothing distinguishes from the unrelated ones above and below. The rail is an inset shadow
 * rather than a thick left border, which the corner radius would taper.
 */
const MatchGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  padding: 12px 14px 14px;
  border-radius: 14px;
  background: ${MATCH_ACCENT.panel};
  border: 1px solid ${MATCH_ACCENT.border};
  box-shadow: inset 3px 0 0 ${MATCH_ACCENT.rail};
`;

// A match with one wave has nothing to group — it keeps the plain run of cards it always had.
const PlainGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;
`;

/**
 * The wave's place in its match's running order, on the card itself. It replaces a "wave 1 of 2"
 * pill: the order is the thing an admin reads a grouped match for, and a number beside the row
 * carries it at a glance where another pill in the badge row did not.
 *
 * A wave that has already had its turn is greyed — what is left to run is what matters.
 */
const OrderMark = ({ index, total, spent }) => (
  <span
    title={`Wave ${index} of ${total} on this match`}
    style={{
      flexShrink: 0,
      marginRight: 10,
      width: 22,
      height: 22,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'CeraPro',
      fontSize: '11.5px',
      fontWeight: 700,
      lineHeight: 1,
      background: spent ? theme.GREY_10 : MATCH_ACCENT.chip,
      color: spent ? theme.GREY_50 : MATCH_ACCENT.rail,
      border: `1px solid ${spent ? theme.GREY_20 : MATCH_ACCENT.border}`
    }}
  >
    {index}
  </span>
);

// Pill-shaped native select — the shared Dropdown wraps long labels inside its fixed-width
// trigger, so the filters use the same styled <select> approach as the quiz GamePicker.
const SelectPill = styled.div`
  position: relative;
  height: 44px;
  border-radius: 999px;
  border: 1px solid ${theme.GREY_30};
  background: white;
  display: flex;
  align-items: center;
  padding: 0 14px;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  font-family: CeraPro;
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  padding-right: 22px;
  white-space: nowrap;
  color: ${theme.GREY_80};

  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 12px;
  pointer-events: none;
  color: ${theme.GREY_60};
  display: flex;
  align-items: center;
`;

const FilterSelect = ({ label, wid, selected, options, onChange }) => (
  <Col noFlex style={{ width: wid }}>
    <Text marg="0 0 4px" b2 med>
      {label}
    </Text>
    <SelectPill>
      <StyledSelect
        value={options.indexOf(selected)}
        onChange={(e) => onChange(options[Number(e.target.value)])}
      >
        {options.map((o, i) => (
          <option key={o.value} value={i}>
            {o.value}
          </option>
        ))}
      </StyledSelect>
      <SelectArrow>
        <FiChevronDown size={14} />
      </SelectArrow>
    </SelectPill>
  </Col>
);

const PageButton = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  background: #fff;
  color: ${theme.GREY_70};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const TextInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  padding: 0 14px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }
`;

// A tinted callout used for the consequences of an action (what it does / does NOT do).
const Callout = ({ tone = 'warning', children }) => {
  const tones = {
    warning: { bg: '#FFF4E5', border: '#FCE4C4', col: '#B45309' },
    danger: { bg: '#FDECEC', border: '#F8D4D4', col: '#DC2626' },
    info: { bg: '#EAF2FE', border: '#D3E3FC', col: '#2563EB' }
  };
  const s = tones[tone] || tones.warning;
  return (
    <Col
      noFlex
      wid="100%"
      pad="11px 13px"
      style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10 }}
    >
      <Text c1 col={s.col} marg="0" style={{ fontSize: 12, lineHeight: 1.5 }}>
        {children}
      </Text>
    </Col>
  );
};

// One modal shell for the three row actions, so they read the same.
const ActionModal = ({ visible, onClose, title, subtitle, children, footer, waiting }) => (
  <Modal
    visible={visible}
    closeModal={onClose}
    wid="540px"
    waiting={waiting}
    noOutsideClick={waiting}
  >
    <Col noFlex wid="100%" pad="28px 26px 24px" gap="14px">
      <Col noFlex wid="100%" gap="4px">
        <Text h3 marg="0">
          {title}
        </Text>
        {subtitle && (
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12.5 }}>
            {subtitle}
          </Text>
        )}
      </Col>
      {children}
      <Row center justifyContent="flex-end" gap="10px" marg="4px 0 0" noFlex wid="100%">
        {footer}
      </Row>
    </Col>
  </Modal>
);

const modeLabel = (mode) => (mode === FANWAVE_MODE.GAME_DAY ? 'Game Day' : 'Non-Game Day');

const fmt = (d, pattern = 'MMM dd, HH:mm') => (d ? format(new Date(d), pattern) : '—');

/** "Home vs Away — MMM dd, HH:mm" for Game Day rows; the window for Non-Game Day rows. */
const matchLabel = (wave) => {
  const g = wave.game;
  if (!g) return 'Match unavailable';
  const names = `${g.homeName || 'TBD'} vs ${g.awayName || 'TBD'}`;
  return g.date ? `${names} — ${fmt(g.date, 'MMM dd, yyyy · HH:mm')}` : names;
};

/**
 * The row's schedule in the terms the wave actually runs on: match minutes read off the live
 * clock, a wall-clock window, or the Half-Time default. Never mixed — those are the three
 * mutually exclusive shapes POST /create accepts.
 */

/** Context line for the confirm dialogs. */
const waveContext = (wave) => {
  if (!wave) return '';
  const who = wave.team?.name || wave.team?.username || 'Channel';
  const what = wave.mode === FANWAVE_MODE.GAME_DAY ? matchLabel(wave) : scheduleLine(wave);
  // A match can hold several waves, so a Game Day wave names its own schedule too — otherwise
  // every wave of that match reads identically in a confirm dialog.
  const run = wave.mode === FANWAVE_MODE.GAME_DAY ? ` · ${scheduleLine(wave)}` : '';
  return `${who} — ${what}${run}`;
};

/** A row's identity: its own waveId, or — for a legacy archive row — the match it ran on. */
const rowKeyOf = (wave, index) => wave.waveId || `${wave.gameId || 'wave'}-${wave.status}-${index}`;

/**
 * The page's rows grouped by MATCH, because a match can hold several waves and they only make
 * sense read together (wave 1 hands over to wave 2). Non-Game Day waves are groups of one.
 *
 * Group order follows the API's own (newest first) by the position each match first appears in;
 * WITHIN a match the waves are ordered the way they run, earliest opening first — which is also
 * the order that says which one is queued behind which.
 */
const groupByMatch = (waves) => {
  const groups = [];
  const byMatch = new Map();
  waves.forEach((wave, index) => {
    const matchKey =
      wave.mode === FANWAVE_MODE.GAME_DAY && wave.gameId ? `match:${wave.gameId}` : null;
    const existing = matchKey ? byMatch.get(matchKey) : null;
    if (existing) {
      existing.waves.push(wave);
      return;
    }
    const group = { key: matchKey || rowKeyOf(wave, index), match: wave, waves: [wave] };
    if (matchKey) byMatch.set(matchKey, group);
    groups.push(group);
  });
  return groups.map((group) => ({
    ...group,
    waves: group.waves.length > 1 ? orderWavesByStart(group.waves) : group.waves,
    queued: queuedWaveIds(group.waves),
    // Waves that can still run. A match's finished/cancelled runs are listed with it, but they
    // are history — only these hand over to one another, so only these make it a queue.
    runnable: group.waves.filter((w) => !isTerminalStatus(w.status)).length
  }));
};

/** DELETE /delete's `wasStatus` → the toast wording. */
const DELETED_LABEL = {
  [WAVE_STATUS.SCHEDULED]: 'an upcoming',
  [WAVE_STATUS.ACTIVE]: 'a live',
  [WAVE_STATUS.COMPLETED]: 'a completed',
  [WAVE_STATUS.CANCELLED]: 'a cancelled',
  [WAVE_STATUS.EXPIRED]: 'an expired'
};

const WaveListSection = ({
  team,
  onTeamChange,
  refreshToken,
  onOpenParticipants,
  pendingParticipantsKey,
  onOpenDetail,
  pendingDetailKey,
  onEdit,
  onCreate,
  rowPatch,
  deleteRequest
}) => {
  const dispatch = useDispatch();

  // Filters + page ride in the URL (?mode=&status=&page=) so the filtered view is
  // shareable and survives refresh. ?team= is owned by the parent screen.
  const [searchParams, setSearchParams] = useSearchParams();
  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const modeParam = searchParams.get('mode') ?? undefined;
  const statusParam = searchParams.get('status') ?? undefined;
  const modeFilter = MODE_OPTIONS.find((o) => o.opt === modeParam) || MODE_OPTIONS[0];
  const statusFilter = STATUS_OPTIONS.find((o) => o.opt === statusParam) || STATUS_OPTIONS[0];
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const filtered = !!(team || modeFilter.opt || statusFilter.opt);

  // Filter changes restart from page 1.
  const setModeFilter = (o) => updateParams({ mode: o.opt, page: undefined });
  const setStatusFilter = (o) => updateParams({ status: o.opt, page: undefined });
  const setPage = (p) => updateParams({ page: p > 1 ? String(p) : undefined });
  // The parent owns ?team= and resets ?page= in the same update, so we just forward the
  // selection — a second setSearchParams here would start from stale params and drop ?team=.
  const handleTeamChange = (t) => onTeamChange(t);

  const [waves, setWaves] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);

  // Disable (Game Day) / Cancel (Non-Game Day).
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Delete — type-to-confirm, and the 409 "cancel/disable it first" recovery path.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [deleteNote, setDeleteNote] = useState(null);
  const [standingDown, setStandingDown] = useState(false);

  // Which row's overflow menu is open (one at a time), keyed by the row's identity.
  const [openMenuKey, setOpenMenuKey] = useState(null);

  // Apply target to a running wave.
  const [applyWave, setApplyWave] = useState(null);
  const [applyValue, setApplyValue] = useState('');
  const [applyError, setApplyError] = useState(null);
  const [applying, setApplying] = useState(false);

  const teamId = team?._id;

  // On deep-link/refresh the list fetches once before ?team= rehydrates (unfiltered) and
  // again after (filtered); without sequencing, an out-of-order response can overwrite the
  // filtered list with all channels. Only the latest request is allowed to commit results.
  const reqIdRef = useRef(0);

  const fetchWaves = useCallback(
    async (isRefetch = false) => {
      const reqId = ++reqIdRef.current;
      if (isRefetch) setRefetching(true);
      else setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_LIMIT) });
      if (teamId) params.set('teamId', teamId);
      if (modeFilter.opt) params.set('mode', modeFilter.opt);
      if (statusFilter.opt) params.set('status', statusFilter.opt);
      const res = await Api.get(`/admin/fanwave/list?${params.toString()}`, null, { silent: true });
      // A newer fetch superseded this one — drop its (stale) results and let the latest win.
      if (reqId !== reqIdRef.current) return;
      if (res?.data) {
        setWaves(res.data.waves || []);
        setTotal(res.data.total || 0);
      }
      if (isRefetch) setRefetching(false);
      else setLoading(false);
    },
    [teamId, modeFilter.opt, statusFilter.opt, page]
  );

  useEffect(() => {
    fetchWaves();
  }, [fetchWaves, refreshToken]);

  // Deep-link/refresh with ?wave=<key>: reopen the participants panel once the matching
  // row is on this page (the panel needs the row for its header context).
  useEffect(() => {
    if (!pendingParticipantsKey || !waves.length) return;
    const match = waves.find(
      (w) =>
        w.waveId === pendingParticipantsKey || (!w.waveId && w.gameId === pendingParticipantsKey)
    );
    if (match) onOpenParticipants(match);
  }, [pendingParticipantsKey, waves, onOpenParticipants]);

  // Same rehydration for the config detail panel (?detail=<key>).
  useEffect(() => {
    if (!pendingDetailKey || !waves.length) return;
    const match = waves.find(
      (w) => w.waveId === pendingDetailKey || (!w.waveId && w.gameId === pendingDetailKey)
    );
    if (match) onOpenDetail(match);
  }, [pendingDetailKey, waves, onOpenDetail]);

  /**
   * POST /cancel. `waveId` cancels that wave in EITHER mode — including a Game Day wave still
   * queued behind the one on air, which is the only way to reach it. Without a waveId the
   * endpoint falls back to "pull whatever Game Day wave is on air", which is all a legacy row
   * with no id of its own can ask for. Neither settles the leaderboard.
   */
  const standDown = async (wave) => {
    const body = { teamId: wave.teamId };
    if (wave.waveId) body.waveId = wave.waveId;
    return Api.post('/admin/fanwave/cancel', body, null, { silent: true });
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    const isGameDay = cancelTarget.mode === FANWAVE_MODE.GAME_DAY;
    setCancelling(true);
    const res = await standDown(cancelTarget);
    setCancelling(false);
    setCancelTarget(null);
    if (!res?.data) {
      // 404 (nothing enabled any more) / 409 (already terminal) — the list moved on under us.
      dispatch({
        type: 'HANDLE_RES',
        payload: {
          err: errorOf(
            res,
            isGameDay ? 'Could not disable the wave.' : 'Could not cancel the wave.'
          )
        }
      });
      fetchWaves(true);
      return;
    }
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: isGameDay ? 'FanWave disabled for the match.' : 'FanWave cancelled.' }
    });
    fetchWaves(true);
  };

  const openDelete = useCallback((wave) => {
    setDeleteTarget(wave);
    setDeleteConfirm('');
    setDeleteError(null);
    setDeleteBlocked(false);
    setDeleteNote(null);
  }, []);

  /**
   * PUT /update echoes the wave's resulting schedule/target/rewards, so an edit patches its row
   * in place — no refetch, and a live wave's running totals stay put. The echoed rewards are
   * fully resolved for both modes, which is neither the stored override nor an activation
   * snapshot, hence REWARDS_SOURCE.RESOLVED.
   */
  useEffect(() => {
    const updated = rowPatch?.wave;
    if (!updated) return;
    setWaves((prev) =>
      prev.map((w) => {
        // Several waves share a match's gameId, so only the waveId can say which row was
        // edited. The gameId fallback is for a legacy row that has no waveId at all — and it
        // is safe precisely because such a match holds exactly one wave.
        const sameWave = updated.waveId
          ? w.waveId === updated.waveId
          : w.mode === updated.mode && w.gameId === updated.gameId && !w.waveId;
        if (!sameWave) return w;
        return {
          ...w,
          startsAt: updated.startsAt ?? null,
          endsAt: updated.endsAt ?? null,
          startMinute: updated.startMinute ?? null,
          endMinute: updated.endMinute ?? null,
          // The resulting schedule must be taken from the echo, not kept: a wave the admin just
          // switched to a clock window would otherwise still claim its previous schedule — and
          // that marker is what the re-opened edit form seeds its fields from. The echo states
          // it directly; the derivation below covers an API that doesn't yet.
          schedule:
            w.mode === FANWAVE_MODE.GAME_DAY
              ? (updated.schedule ??
                (updated.startMinute != null || updated.endMinute != null
                  ? GAME_DAY_SCHEDULE.MINUTES
                  : updated.startsAt
                    ? GAME_DAY_SCHEDULE.WINDOW
                    : GAME_DAY_SCHEDULE.DEFAULT))
              : w.schedule,
          tapTarget: updated.tapTarget ?? w.tapTarget,
          rewards: updated.rewards ?? w.rewards,
          rewardsSource: updated.rewards ? REWARDS_SOURCE.RESOLVED : w.rewardsSource
        };
      })
    );
  }, [rowPatch]);

  // The edit panel hit a 409 (nothing left to update) and handed the wave over to be deleted.
  useEffect(() => {
    if (deleteRequest?.wave) openDelete(deleteRequest.wave);
  }, [deleteRequest, openDelete]);

  // An open overflow menu closes on the next click anywhere else, on Escape, and whenever the
  // rows underneath it change (a refetch can reorder or drop the row it belongs to).
  useEffect(() => {
    if (!openMenuKey) return;
    const close = () => setOpenMenuKey(null);
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuKey]);

  useEffect(() => {
    setOpenMenuKey(null);
  }, [waves]);

  const closeDelete = () => {
    if (deleting || standingDown) return;
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    // teamId + EXACTLY ONE identifier, chosen by `deleteKeyOf`'s precedence — sending two is
    // a 400.
    const key = deleteKeyOf(deleteTarget);
    if (!key) return;
    const params = new URLSearchParams({ teamId: deleteTarget.teamId, [key.param]: key.value });

    setDeleting(true);
    setDeleteError(null);
    const res = await Api.delete(`/admin/fanwave/delete?${params.toString()}`, null, null, {
      silent: true,
      raw: true
    });
    setDeleting(false);

    if (!res?.data) {
      setDeleteError(errorOf(res, 'Could not delete the wave.'));
      // 409 — the wave went live (or was already live). Offer the stand-down inline instead
      // of leaving the admin at a dead end; the API's status is the source of truth here, so
      // a row can go live between render and click.
      setDeleteBlocked(res?.status === 409);
      setDeleteNote(null);
      return;
    }
    dispatch({
      type: 'HANDLE_RES',
      payload: {
        res:
          key.kind === 'history'
            ? 'Deleted one completed run.'
            : `Deleted ${DELETED_LABEL[res.data.wasStatus] || 'a'} wave.`
      }
    });
    setDeleteTarget(null);
    fetchWaves(true);
  };

  /** The 409 recovery: disable/cancel the live wave, then let them press Delete again. */
  const handleStandDownThenRetry = async () => {
    if (!deleteTarget) return;
    const isGameDay = deleteTarget.mode === FANWAVE_MODE.GAME_DAY;
    setStandingDown(true);
    const res = await standDown(deleteTarget);
    setStandingDown(false);
    if (!res?.data) {
      setDeleteError(
        errorOf(res, isGameDay ? 'Could not disable the wave.' : 'Could not cancel the wave.')
      );
      return;
    }
    setDeleteError(null);
    setDeleteBlocked(false);
    setDeleteNote(
      isGameDay
        ? 'Wave disabled — it is closed for connected fans. You can delete it now.'
        : 'Wave cancelled — it is closed for connected fans. You can delete it now.'
    );
    // The row's status is stale now; refresh behind the modal.
    fetchWaves(true);
  };

  const openApply = (wave) => {
    setApplyWave(wave);
    setApplyValue(wave.tapTarget != null ? String(wave.tapTarget) : '');
    setApplyError(null);
  };

  const handleApply = async () => {
    if (!applyWave) return;
    const err = validateTapTarget(applyValue, TAP_TARGET_BOUNDS);
    if (applyValue === '' || err) {
      setApplyError(err || 'Enter the new tap target');
      return;
    }
    setApplying(true);
    // `confirm: true` is the endpoint's explicit "Apply to active FanWave?" acknowledgement —
    // this dialog is it.
    const res = await Api.post(
      '/admin/fanwave/applyTarget',
      { teamId: applyWave.teamId, target: Number(applyValue), confirm: true },
      null,
      { silent: true }
    );
    setApplying(false);
    if (!res?.data) {
      setApplyError(errorOf(res, 'Could not apply the target.'));
      return;
    }
    setApplyWave(null);
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: `Target applied — fans now see ${res.data.tapTarget} taps.` }
    });
    fetchWaves(true);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const cancelIsDisable = cancelTarget?.mode === FANWAVE_MODE.GAME_DAY;
  const deleteIsGameDay = deleteTarget?.mode === FANWAVE_MODE.GAME_DAY;
  const deleteConfirmed = deleteConfirm.trim().toUpperCase() === DELETE_KEYWORD;
  // Which identifier this delete will use. `game` is the legacy shorthand — the row carries no
  // waveId — and the API refuses it outright once the match holds several waves, so the dialog
  // says how many are listed rather than implying they all go.
  // The match's other waves that can still run — what happens to the match after this one is
  // pulled depends on whether anything is behind it.
  const cancelSuccessors =
    cancelTarget?.mode === FANWAVE_MODE.GAME_DAY && cancelTarget?.gameId
      ? waves.filter(
          (w) =>
            w.gameId === cancelTarget.gameId &&
            w.waveId !== cancelTarget.waveId &&
            !isTerminalStatus(w.status)
        ).length
      : 0;
  const deleteKind = deleteKeyOf(deleteTarget)?.kind;
  const matchWaveCount =
    deleteKind === 'game' ? waves.filter((w) => w.gameId === deleteTarget.gameId).length : 0;

  return (
    <Col noFlex wid="100%" gap="14px">
      {/* Filters */}
      <Row start gap="16px" wid="100%" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Col noFlex>
          <Text marg="0 0 4px" b2 med>
            Channel
          </Text>
          <TeamPicker team={team} onChange={handleTeamChange} label={null} />
        </Col>
        <FilterSelect
          label="Mode"
          wid="170px"
          selected={modeFilter}
          options={MODE_OPTIONS}
          onChange={setModeFilter}
        />
        <FilterSelect
          label="Status"
          wid="170px"
          selected={statusFilter}
          options={STATUS_OPTIONS}
          onChange={setStatusFilter}
        />
        <Row noFlex center start gap="12px" style={{ marginLeft: 'auto', paddingBottom: 10 }}>
          <Text c2 col={theme.GREY_60}>
            {total} wave{total === 1 ? '' : 's'}
          </Text>
          <RefreshButton
            type="button"
            $spinning={refetching ? 1 : 0}
            disabled={refetching || loading}
            onClick={() => fetchWaves(true)}
            title="Refresh"
          >
            <FiRefreshCw size={13} />
            Refresh
          </RefreshButton>
        </Row>
      </Row>

      {/* Rows */}
      {loading ? (
        <Row centerAll pad="40px 0">
          <Spinner2 />
        </Row>
      ) : !waves.length ? (
        <Col
          centerAll
          noFlex
          wid="100%"
          pad="48px 0"
          gap="6px"
          style={{
            borderRadius: '14px',
            border: `1px dashed ${theme.GREY_30}`,
            background: theme.GREY_10
          }}
        >
          <Text h4 col={theme.GREY_70} marg="0">
            {filtered ? 'No waves match these filters' : 'No FanWaves are scheduled'}
          </Text>
          <Text
            c1
            col={theme.GREY_60}
            marg="0"
            center
            style={{ fontSize: '12.5px', maxWidth: 430, lineHeight: 1.5 }}
          >
            {filtered
              ? 'Adjust the filters, or create a FanWave for this channel.'
              : 'A FanWave runs only once an admin creates one — nothing is scheduled for a fixture on its own. Create one to get started.'}
          </Text>
          {onCreate && (
            <Button
              text="+ Create FanWave"
              onClick={onCreate}
              bg={theme.SECONDARY}
              small
              wid="170px"
              marg="10px 0 0"
            />
          )}
        </Col>
      ) : (
        <Col wid="100%" gap="10px" noFlex>
          {groupByMatch(waves).map((group) => {
            // Several waves on one fixture are enclosed and accented so they read as one match;
            // a lone wave keeps the plain card it always had, since there is nothing to group.
            const grouped = group.waves.length > 1;
            const Shell = grouped ? MatchGroup : PlainGroup;
            return (
              <Shell key={group.key}>
                {/* A match with several rows gets one header for the fixture, so the waves
                  underneath read as its running order rather than as unrelated rows. The badge
                  only claims a QUEUE when two or more waves can still run: a match usually
                  carries its past runs alongside the one that is scheduled. */}
                {grouped && (
                  <Col noFlex wid="100%" gap="3px" pad="0 2px">
                    <Row center start noFlex gap="8px" style={{ flexWrap: 'wrap' }}>
                      <Text b2 bold col={MATCH_ACCENT.rail} marg="0">
                        {matchLabel(group.match)}
                      </Text>
                      {group.runnable > 1 ? (
                        <Badge variant="info">{group.runnable} waves, one after another</Badge>
                      ) : (
                        <Badge variant="muted">
                          {group.waves.length} waves on this match
                          {group.runnable === 1 ? ' · 1 still to run' : ' · none still to run'}
                        </Badge>
                      )}
                    </Row>
                    {/* Says the thing the enclosure implies, for anyone reading the list for the
                      first time — and which of the two it is: a queue still to come, or the
                      fixture's history. */}
                    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
                      {group.runnable > 1
                        ? 'All on this one match — each wave opens as the one before it closes, in this order.'
                        : 'All on this one match — every FanWave it has held, in the order they ran.'}
                    </Text>
                  </Col>
                )}
                {group.waves.map((wave, i) => {
                  const isGameDay = wave.mode === FANWAVE_MODE.GAME_DAY;
                  const isLive = wave.status === WAVE_STATUS.ACTIVE;
                  const isUpcoming = wave.status === WAVE_STATUS.SCHEDULED;
                  // Created, but another wave of this match is still ahead of it: the lifecycle
                  // cron promotes it as that one closes. It stays fully editable meanwhile.
                  const isQueued = wave.waveId ? group.queued.has(wave.waveId) : false;
                  // PUT /update takes scheduled and active waves; a live one can still change its
                  // end, target and rewards (never its start).
                  const editable =
                    (isUpcoming || isLive) && !!(wave.waveId || (isGameDay && wave.gameId));
                  // Disable (Game Day) / Cancel (Non-Game Day) — before or during the wave, and
                  // for a queued one this is the only way to reach it.
                  const stoppable = isLive || isUpcoming;
                  // Delete stays available on every row: completed, cancelled and expired waves
                  // are all deletable, a live one answers 409 (which the dialog handles), and a
                  // row can go live between render and click.
                  const deletable = !!deleteKeyOf(wave);
                  // Row identity, shared by React's key and the overflow menu's open state —
                  // the waveId, which is what tells a match's several waves apart.
                  const rowKey = rowKeyOf(wave, i);
                  return (
                    <WaveCard
                      key={rowKey}
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenDetail(wave)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenDetail(wave);
                        }
                      }}
                      title="View configuration"
                    >
                      {/* Team + schedule — `start` pins content left (Row's justify-content
                    otherwise inherits the card's space-between and pushes it right). */}
                      <Row noFlex center start style={{ flex: '1 1 0', minWidth: 0 }}>
                        {/* Where this wave comes in its match's order — see OrderMark. */}
                        {grouped && (
                          <OrderMark
                            index={i + 1}
                            total={group.waves.length}
                            spent={isTerminalStatus(wave.status)}
                          />
                        )}
                        <Avatar small user={wave.team} avatar={wave.team?.avatar} />
                        <Col
                          marg="0 0 0 10px"
                          noFlex
                          style={{ minWidth: 0, alignItems: 'flex-start' }}
                        >
                          <Row noFlex center start gap="8px" style={{ flexWrap: 'wrap' }}>
                            <Text
                              med
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {wave.team?.name || wave.team?.username || 'Unknown channel'}
                            </Text>
                            <Badge variant={isGameDay ? 'locked' : 'neutral'}>
                              {modeLabel(wave.mode)}
                            </Badge>
                            <Badge variant={STATUS_BADGE_VARIANT[wave.status] || 'neutral'} dot>
                              {wave.status}
                            </Badge>
                            {/* Still `scheduled` like any other upcoming wave, but it cannot open
                          until the wave ahead of it on this match closes. */}
                            {isQueued && <Badge variant="warning">queued</Badge>}
                            {/* A session started by auto restart says which one it is; the
                          first session of an auto-restarting wave is tagged too. */}
                            {wave.restartIndex > 0 ? (
                              <Badge variant="info">Restart {wave.restartIndex}</Badge>
                            ) : (
                              wave.autoRestart && <Badge variant="info">Auto restart</Badge>
                            )}
                          </Row>
                          {/* The fixture is the group's header when a match runs several waves,
                            so repeating it on every card would just be noise. */}
                          {isGameDay && !grouped && (
                            <Text c2 col={theme.GREY_60} marg="4px 0 0">
                              {matchLabel(wave)}
                            </Text>
                          )}
                          <Text
                            c2
                            col={isGameDay ? theme.GREY_50 : theme.GREY_60}
                            marg={isGameDay ? '2px 0 0' : '4px 0 0'}
                          >
                            {scheduleLine(wave)}
                          </Text>
                        </Col>
                      </Row>

                      {/* Numbers */}
                      <Row noFlex center start gap="22px">
                        <Col
                          noFlex
                          style={{ alignItems: 'flex-end' }}
                          title={missingTapTargetReason(wave)}
                        >
                          <Text c2 col={theme.GREY_50} marg="0">
                            Target
                          </Text>
                          <Text b2 bold marg="0">
                            {wave.tapTarget ?? '—'}
                          </Text>
                        </Col>
                        <Col noFlex style={{ alignItems: 'flex-end' }}>
                          <Text c2 col={theme.GREY_50} marg="0">
                            Taps{isLive ? ' (live)' : ''}
                          </Text>
                          <Text b2 bold marg="0" col={isLive ? theme.SECONDARY : undefined}>
                            {wave.totalHits ?? 0}
                          </Text>
                        </Col>
                        <Col noFlex style={{ alignItems: 'flex-end' }}>
                          <Text c2 col={theme.GREY_50} marg="0">
                            Fans
                          </Text>
                          <Text b2 bold marg="0">
                            {wave.totalParticipants ?? 0}
                          </Text>
                        </Col>
                      </Row>

                      {/* Actions — the two everyday ones inline, the state-specific ones in the
                    overflow menu so the cluster stays one line at any row width. */}
                      <Row noFlex center gap="8px" style={{ justifyContent: 'flex-end' }}>
                        <RowActionButton
                          type="button"
                          disabled={!wave.waveId && !wave.gameId}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenParticipants(wave);
                          }}
                          title={
                            wave.waveId || wave.gameId
                              ? 'View participants'
                              : 'No participation records for this wave'
                          }
                        >
                          Participants
                        </RowActionButton>

                        {/* Edit stays put whatever the status — a terminal wave explains itself
                      instead of the button vanishing. */}
                        <RowActionButton
                          type="button"
                          disabled={!editable}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit && onEdit(wave);
                          }}
                          title={
                            editable
                              ? isLive
                                ? 'Change where this running wave ends, its target or its rewards'
                                : 'Change this wave’s schedule, target or rewards'
                              : `A ${wave.status} wave can no longer be edited — delete it instead`
                          }
                        >
                          Edit
                        </RowActionButton>

                        <MenuWrap>
                          <MenuTrigger
                            type="button"
                            $open={openMenuKey === rowKey}
                            aria-haspopup="menu"
                            aria-expanded={openMenuKey === rowKey}
                            title="More actions"
                            // mousedown closes any open menu document-wide, so toggle on that same
                            // event — a click handler here would reopen what the close just shut.
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              setOpenMenuKey(openMenuKey === rowKey ? null : rowKey);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FiMoreHorizontal size={16} />
                          </MenuTrigger>

                          {openMenuKey === rowKey && (
                            <ActionMenu
                              role="menu"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isLive && (
                                <MenuItem
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenMenuKey(null);
                                    openApply(wave);
                                  }}
                                >
                                  Apply target…
                                </MenuItem>
                              )}
                              {stoppable && (
                                <MenuItem
                                  type="button"
                                  role="menuitem"
                                  $danger
                                  onClick={() => {
                                    setOpenMenuKey(null);
                                    setCancelTarget(wave);
                                  }}
                                >
                                  {isGameDay ? 'Disable wave…' : 'Cancel wave…'}
                                </MenuItem>
                              )}
                              {(isLive || stoppable) && <MenuDivider />}
                              <MenuItem
                                type="button"
                                role="menuitem"
                                $danger
                                disabled={!deletable}
                                title={
                                  deletable
                                    ? undefined
                                    : 'The API returned this row without an id to delete it by'
                                }
                                onClick={() => {
                                  setOpenMenuKey(null);
                                  openDelete(wave);
                                }}
                              >
                                Delete wave…
                              </MenuItem>
                              {/* Nothing to target: the row arrived with neither a waveId nor a
                            gameId, so no delete request can be built for it. Say so — a grey
                            item with no reason reads as a broken button. */}
                              {!deletable && (
                                <MenuNote>
                                  This row has no id to delete by — the list returned it without a
                                  waveId or gameId (its match record is missing).
                                </MenuNote>
                              )}
                            </ActionMenu>
                          )}
                        </MenuWrap>
                      </Row>
                    </WaveCard>
                  );
                })}
              </Shell>
            );
          })}
        </Col>
      )}

      {/* Pagination */}
      {total > PAGE_LIMIT && (
        <Row center justifyContent="center" gap="14px" pad="4px 0" noFlex wid="100%">
          <PageButton type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <FiChevronLeft size={15} />
          </PageButton>
          <Text c1 col={theme.GREY_60} marg="0">
            Page {page} of {totalPages}
          </Text>
          <PageButton type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            <FiChevronRight size={15} />
          </PageButton>
        </Row>
      )}

      {/* ——— Disable (Game Day) / Cancel (Non-Game Day) ——— */}
      <ActionModal
        visible={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        title={cancelIsDisable ? 'Disable this FanWave?' : 'Cancel this FanWave?'}
        subtitle={waveContext(cancelTarget)}
        waiting={cancelling}
        footer={
          <>
            <Button
              text="Keep it"
              onClick={() => !cancelling && setCancelTarget(null)}
              bordered
              disabled={cancelling}
              style={{ width: 'fit-content', opacity: 0.85 }}
            />
            <Button
              text={
                cancelling
                  ? cancelIsDisable
                    ? 'Disabling…'
                    : 'Cancelling…'
                  : cancelIsDisable
                    ? 'Disable wave'
                    : 'Cancel wave'
              }
              onClick={handleCancel}
              bg={theme.SECONDARY}
              loading={cancelling}
              disabled={cancelling}
              style={{ width: 'fit-content' }}
            />
          </>
        }
      >
        <Text b2 col={theme.GREY_70} marg="0" style={{ fontSize: 13, lineHeight: 1.55 }}>
          {cancelTarget?.status === WAVE_STATUS.ACTIVE
            ? 'The wave is live — this archives it and closes it immediately for every connected fan.'
            : cancelIsDisable
              ? 'The wave is archived and never opens for this match — including one still queued behind another, which this is the only way to remove from the queue.'
              : 'The scheduled wave is marked cancelled and never activates.'}
          {cancelIsDisable &&
            (cancelSuccessors > 0
              ? ` The match still has ${cancelSuccessors} other wave${
                  cancelSuccessors === 1 ? '' : 's'
                } configured — the next one opens at its own start time.`
              : ' The match then stays without a FanWave until an admin enables one again.')}
        </Text>
        <Callout tone="warning">
          This does <b>not</b> settle the leaderboard. Rank rewards and the community bonus are only
          paid when a wave closes on its own. Points fans have already claimed for their taps are
          unaffected.
        </Callout>
      </ActionModal>

      {/* ——— Delete (hard confirm; 409 recovery inline) ——— */}
      <ActionModal
        visible={!!deleteTarget}
        onClose={closeDelete}
        title="Delete this FanWave?"
        subtitle={waveContext(deleteTarget)}
        waiting={deleting || standingDown}
        footer={
          <>
            <Button
              text="Keep it"
              onClick={closeDelete}
              bordered
              disabled={deleting || standingDown}
              style={{ width: 'fit-content', opacity: 0.85 }}
            />
            {deleteBlocked && (
              <Button
                text={
                  standingDown
                    ? deleteIsGameDay
                      ? 'Disabling…'
                      : 'Cancelling…'
                    : deleteIsGameDay
                      ? 'Disable wave'
                      : 'Cancel wave'
                }
                onClick={handleStandDownThenRetry}
                bordered
                loading={standingDown}
                disabled={standingDown || deleting}
                style={{ width: 'fit-content' }}
              />
            )}
            <Button
              text={deleting ? 'Deleting…' : 'Delete wave'}
              onClick={handleDelete}
              bg={theme.SECONDARY}
              loading={deleting}
              disabled={!deleteConfirmed || deleting || standingDown}
              style={{ width: 'fit-content' }}
            />
          </>
        }
      >
        <Text b2 col={theme.GREY_70} marg="0" style={{ fontSize: 13, lineHeight: 1.55 }}>
          {deleteKind === 'game'
            ? 'This row has no id of its own, so it is deleted by its match — which the API allows only while that match holds exactly one wave. The wave goes everywhere the CMS reads it: its schedule, the session it occupies and its archived history row.'
            : 'This removes the wave everywhere the CMS reads it — its schedule, the live session it occupies and its archived history row. The row disappears from this list entirely. Other waves on the same match are left alone.'}
        </Text>
        {deleteKind === 'game' && matchWaveCount > 1 && (
          <Callout tone="warning">
            This match has {matchWaveCount} waves listed, and this row can only be addressed by the
            match — so the API will refuse the delete as ambiguous. Delete the wave from a row that
            carries its own id instead.
          </Callout>
        )}
        <Callout tone="danger">
          Irreversible. Fans’ participation records (taps, claimed points) and points already paid
          out are kept.
        </Callout>
        {deleteError && (
          <Callout tone={deleteBlocked ? 'warning' : 'danger'}>
            {deleteError}
            {deleteBlocked && (
              <>
                {' '}
                {deleteIsGameDay ? 'Disable' : 'Cancel'} it below, then delete — note that doing so
                does not settle the leaderboard.
              </>
            )}
          </Callout>
        )}
        {deleteNote && <Callout tone="info">{deleteNote}</Callout>}
        <Col noFlex wid="100%" gap="6px">
          <Text b2 bold marg="0" style={{ fontSize: 13 }}>
            Type {DELETE_KEYWORD} to confirm
          </Text>
          <TextInput
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={DELETE_KEYWORD}
            autoComplete="off"
            spellCheck={false}
          />
        </Col>
      </ActionModal>

      {/* ——— Apply target to a running wave ——— */}
      <ActionModal
        visible={!!applyWave}
        onClose={() => !applying && setApplyWave(null)}
        title="Apply target to the live FanWave?"
        subtitle={waveContext(applyWave)}
        waiting={applying}
        footer={
          <>
            <Button
              text="Cancel"
              onClick={() => !applying && setApplyWave(null)}
              bordered
              disabled={applying}
              style={{ width: 'fit-content', opacity: 0.85 }}
            />
            <Button
              text={applying ? 'Applying…' : 'Apply target'}
              onClick={handleApply}
              bg={theme.SECONDARY}
              loading={applying}
              disabled={applying}
              style={{ width: 'fit-content' }}
            />
          </>
        }
      >
        <Text b2 col={theme.GREY_70} marg="0" style={{ fontSize: 13, lineHeight: 1.55 }}>
          The new target is broadcast to every connected fan and rescales their progress bar
          straight away. Current target: {applyWave?.tapTarget ?? '—'}; {applyWave?.totalHits ?? 0}{' '}
          taps so far.
        </Text>
        <Col noFlex wid="100%" gap="6px" style={{ maxWidth: 220 }}>
          <Text b2 bold marg="0" style={{ fontSize: 13 }}>
            New tap target
          </Text>
          <TextInput
            type="number"
            inputMode="numeric"
            value={applyValue}
            onChange={(e) => setApplyValue(toDigits(e.target.value))}
            onKeyDown={blockNonInteger}
            hasError={!!applyError}
          />
          <Text
            c1
            col={applyError ? theme.SECONDARY : theme.GREY_60}
            marg="0"
            style={{ fontSize: 11.5 }}
          >
            {applyError ||
              `Min ${fmtCount(TAP_TARGET_BOUNDS.min)}, max ${fmtCount(
                TAP_TARGET_BOUNDS.max
              )}, in steps of ${fmtCount(TAP_TARGET_BOUNDS.step)}.`}
          </Text>
        </Col>
      </ActionModal>
    </Col>
  );
};

export default WaveListSection;
